/**
 * @fileoverview Utilities for validating JPEG images and their dimensions
 *
 * This module provides functions for validating JPEG images, specifically:
 * - Checking if a JPEG is baseline encoded
 * - Validating image dimensions against specified requirements
 *
 * @module Images
 */

import { ErrorConstants, MediaConstants } from 'src/constants';
import { VALIDATION_CODES } from 'src/constants/validationCodes';
import { ValidationError } from 'src/errors/CustomErrors';
import { logger } from 'src/logger';

import { loadImageDimensions } from '../MediaUtils';
import { DimensionSpecs } from '../Types';
import { validateAspectRatio, validateDimensions } from '../ValidationUtils';

/**
 * Checks if a buffer contains a baseline JPEG signature by validating the file header
 * and scanning for Start of Frame (SOF) markers.
 *
 * This function analyzes the binary structure of a JPEG file to determine if it's
 * a baseline JPEG (SOF0 marker).
 * It validates the file header, checks for proper segment structure, and looks
 * for the baseline DCT marker.
 *
 * @param buffer - Uint8Array containing the JPEG file data
 * @returns true if the file is a baseline JPEG, false if not
 * @example
 * ```typescript
 * const jpegData = new Uint8Array(await file.arrayBuffer());
 * if (isBaselineJpeg(jpegData)) {
 *   console.log('Valid baseline JPEG detected');
 * }
 * ```
 * @see {@link https://en.wikipedia.org/wiki/JPEG#Syntax_and_structure} JPEG File Structure and Markers
 */
export const isBaselineJpeg = (buffer: Uint8Array): boolean => {
  // Create a DataView to read binary data from the buffer
  const view = new DataView(buffer.buffer);

  // Check for valid JPEG signature (0xFFD8)
  if (view.byteLength < 2 || view.getUint16(0) !== MediaConstants.JPEG.SIGNATURE) {
    logger.error(ErrorConstants.MESSAGES.INVALID_JPEG_HEADER);
    return false;
  }

  // Start parsing after the JPEG signature
  let offset = 2;

  // Iterate through the JPEG segments
  while (offset < buffer.length) {
    // Ensure we have enough bytes to read a marker
    if (offset + 2 > buffer.length) {
      logger.error(ErrorConstants.MESSAGES.INCOMPLETE_MARKER);
      return false;
    }

    // All JPEG markers must start with 0xFF
    if (buffer[offset] !== MediaConstants.JPEG.MARKER.START) {
      logger.error(ErrorConstants.MESSAGES.INVALID_MARKER);
      return false;
    }

    // Get the marker code (second byte of the marker)
    const markerCode = buffer[offset + 1];

    // End of Image marker - no SOF found
    if (markerCode === MediaConstants.JPEG.MARKER.EOI) {
      logger.error(ErrorConstants.MESSAGES.NO_SOF_MARKER);
      return false;
    }

    // Skip Restart markers (RST0-RST7: FF D0 - FF D7)
    if (
      markerCode >= MediaConstants.JPEG.MARKER.RST.START &&
      markerCode <= MediaConstants.JPEG.MARKER.RST.END
    ) {
      offset += 2;
      continue;
    }

    // Ensure we have enough bytes to read the segment length
    if (offset + 4 > buffer.length) {
      logger.error(ErrorConstants.MESSAGES.INCOMPLETE_SEGMENT);
      return false;
    }

    // Read the segment length (includes the 2 bytes of the length field itself)
    const segmentLength = view.getUint16(offset + 2);

    // Segment length must be at least 2 bytes (for the length field itself)
    if (segmentLength < 2) {
      logger.error(ErrorConstants.MESSAGES.INVALID_SEGMENT_LENGTH);
      return false;
    }

    // Ensure the segment doesn't extend beyond the buffer
    if (offset + 2 + segmentLength > buffer.length) {
      logger.error(ErrorConstants.MESSAGES.INCOMPLETE_SEGMENT);
      return false;
    }

    // Found Start of Frame marker for Baseline DCT (SOF0) - success!
    if (markerCode === MediaConstants.JPEG.MARKER.SOF0) return true;

    // Found Start of Frame marker for Progressive DCT (SOF2) - not baseline
    if (markerCode === MediaConstants.JPEG.MARKER.SOF2) {
      logger.error(ErrorConstants.MESSAGES.PROGRESSIVE_JPEG_ERROR);
      return false;
    }

    // Move to the next segment
    offset += 2 + segmentLength;
  }

  // Reached end of file without finding a Start of Frame marker
  logger.error(ErrorConstants.MESSAGES.NO_SOF_MARKER);
  return false;
};

/**
 * Validates if a JPEG file is baseline encoded
 *
 * This function checks if the provided file is a valid JPEG image and if it's
 * baseline encoded (not progressive). It first validates the file's MIME type,
 * then reads the file content and passes it to {@link isBaselineJpeg} for analysis.
 *
 * @param fileObject - File object to validate
 * @throws {ValidationError} When file is not a valid JPEG or not baseline encoded
 * @returns Promise that resolves if validation passes
 * @example
 * ```typescript
 * try {
 *   await validateBaselineJpeg(fileInput.files[0]);
 *   console.log('JPEG validation passed');
 * } catch (error) {
 *   console.error('JPEG validation failed:', error.message);
 * }
 * ```
 */
export const validateBaselineJpeg = async (fileObject: File): Promise<void> => {
  try {
    if (!fileObject.type.match(MediaConstants.JPEG.MIME_REGEX)) {
      throw new ValidationError(
        ErrorConstants.MESSAGES.INVALID_JPEG_TYPE,
        VALIDATION_CODES.INVALID_JPEG_TYPE,
      );
    }

    const buffer = await fileObject.arrayBuffer();
    if (!isBaselineJpeg(new Uint8Array(buffer))) {
      throw new ValidationError(
        ErrorConstants.MESSAGES.INVALID_BASELINE_JPEG,
        VALIDATION_CODES.INVALID_BASELINE_JPEG,
      );
    }
  } catch (error) {
    // Log the error
    logger.error(ErrorConstants.MESSAGES.GENERAL_IMAGE_VALIDATION_ERROR, { error });

    // Rethrow ValidationErrors as-is, but wrap other errors
    if (error instanceof ValidationError) {
      throw error;
    }
    throw new ValidationError(
      `${ErrorConstants.MESSAGES.GENERAL_IMAGE_VALIDATION_ERROR} : ${(error as Error).message}`,
    );
  }
};

/**
 * Validates image dimensions against specifications
 *
 * This function checks if an image file meets the specified dimension requirements.
 * It validates:
 * - If the file is an image (based on MIME type)
 * - If the image dimensions can be determined
 * - If the dimensions meet the minimum/maximum width/height requirements
 * - If the aspect ratio is within the specified tolerance (when applicable)
 *
 * @param {File} imageFile - File object containing the image to validate
 * @param {DimensionSpecs} [specs] - Dimension specifications to validate against
 * @throws {ValidationError} When dimensions don't meet requirements or file is not an image
 * @returns {Promise<void>} Promise that resolves if validation passes
 * @example
 * ```typescript
 * const dimensionSpecs = {
 *   minWidth: 800,
 *   minHeight: 600,
 *   maxWidth: 1920,
 *   maxHeight: 1080,
 *   aspectRatio: 16/9,
 *   aspectRatioTolerance: 0.01
 * };
 *
 * try {
 *   await validateImageDimensions(imageFile, dimensionSpecs);
 *   console.log('Image dimensions are valid');
 * } catch (error) {
 *   console.error('Image validation failed:', error.message);
 * }
 * ```
 */
export const validateImageDimensions = async (
  imageFile: File,
  specs: DimensionSpecs,
): Promise<void> => {
  if (!imageFile.type.startsWith(MediaConstants.MIME.PREFIXES.IMAGE)) {
    throw new ValidationError(
      ErrorConstants.MESSAGES.INVALID_FILE_TYPE,
      VALIDATION_CODES.INVALID_FILE_TYPE,
    );
  }

  const objectUrl = URL.createObjectURL(imageFile);
  try {
    const dimensions = await loadImageDimensions(objectUrl);
    if (dimensions.width <= 0 || dimensions.height <= 0) {
      throw new ValidationError('Could not determine file dimensions');
    }
    validateDimensions(dimensions.width, dimensions.height, specs);

    // Check if aspectRatio exists and is not null/undefined before validating
    if (specs?.aspectRatio) {
      validateAspectRatio(
        dimensions.width,
        dimensions.height,
        specs.aspectRatio,
        specs.aspectRatioTolerance,
      );
    }
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
